import { motion } from "framer-motion";
import Marquee from "react-fast-marquee";
import { ArrowDownRight } from "lucide-react";
import { PROFILE, STATS } from "../../data/portfolio";

const lineVariants = {
  hidden: { y: "110%" },
  visible: (i) => ({
    y: "0%",
    transition: { duration: 1, delay: 0.3 + i * 0.12, ease: [0.22, 1, 0.36, 1] },
  }),
};

export const Hero = () => {
  const go = (href) => document.querySelector(href)?.scrollIntoView({ behavior: "smooth" });

  return (
    <section
      id="top"
      className="relative min-h-screen flex flex-col justify-between pt-32 pb-6 overflow-hidden"
      data-testid="hero-section"
    >
      {/* glowing orb + grid */}
      <div className="absolute inset-0 grid-lines opacity-60" />
      <motion.div
        aria-hidden
        initial={{ opacity: 0, scale: 0.8 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 2 }}
        className="absolute -top-40 -right-40 w-[650px] h-[650px] rounded-full blur-[130px]"
        style={{ background: "radial-gradient(circle, rgba(94,126,155,0.16), transparent 70%)" }}
      />
      <div
        aria-hidden
        className="absolute bottom-20 -left-40 w-[500px] h-[500px] rounded-full blur-[140px]"
        style={{ background: "radial-gradient(circle, rgba(138,161,137,0.16), transparent 70%)" }}
      />

      <div className="relative max-w-[1400px] w-full mx-auto px-6 md:px-12 flex-1 flex flex-col justify-center">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2 }}
          className="flex items-center gap-4 mb-8"
        >
          <span className="relative flex h-2 w-2">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#8AA189] opacity-75" />
            <span className="relative inline-flex rounded-full h-2 w-2 bg-[#8AA189]" />
          </span>
          <span className="font-mono text-xs uppercase tracking-[0.3em] text-[#5B6472]">
            {PROFILE.availability}
          </span>
        </motion.div>

        <h1 className="font-display font-bold uppercase leading-[0.85] tracking-tighter text-[16vw] md:text-[13vw] lg:text-[11rem]">
          {PROFILE.role.map((word, i) => (
            <span key={word} className="block overflow-hidden">
              <motion.span
                custom={i}
                variants={lineVariants}
                initial="hidden"
                animate="visible"
                className={`block ${i === 1 ? "text-[#5E7E9B] text-glow" : "text-[#1F2D3D]"}`}
                data-testid={`hero-line-${i}`}
              >
                {word}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1, duration: 0.9 }}
          className="mt-10 flex flex-col lg:flex-row lg:items-end justify-between gap-8"
        >
          <p className="max-w-xl text-[#5B6472] text-base md:text-lg leading-relaxed">
            {PROFILE.tagline}
          </p>
          <button
            onClick={() => go("#work")}
            className="group inline-flex items-center gap-3 text-[#1F2D3D] font-display text-xl shrink-0"
            data-testid="hero-cta"
          >
            <span className="border-b border-[#1F2D3D]/25 group-hover:border-[#5E7E9B] group-hover:text-[#5E7E9B] transition-colors duration-300 pb-1">
              View selected work
            </span>
            <span className="w-11 h-11 rounded-full border border-[#1F2D3D]/15 grid place-items-center group-hover:bg-[#5E7E9B] group-hover:text-white group-hover:border-[#5E7E9B] transition-colors duration-300">
              <ArrowDownRight size={18} className="group-hover:rotate-0 -rotate-45 transition-transform duration-300" />
            </span>
          </button>
        </motion.div>
      </div>

      {/* stats row */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.4 }}
        className="relative max-w-[1400px] w-full mx-auto px-6 md:px-12 grid grid-cols-2 md:grid-cols-4 gap-px bg-[#1F2D3D]/[0.06] border-y border-[#1F2D3D]/10 mt-10"
      >
        {STATS.map((s) => (
          <div key={s.label} className="bg-white shadow-[0_10px_30px_-15px_rgba(31,45,61,0.15)] px-4 py-6" data-testid={`stat-${s.label}`}>
            <div className="font-display text-3xl md:text-4xl text-[#1F2D3D]">{s.value}</div>
            <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#7A8492] mt-2">
              {s.label}
            </div>
          </div>
        ))}
      </motion.div>

      {/* marquee */}
      <div className="relative mt-6 border-y border-[#1F2D3D]/10 py-4 overflow-hidden">
        <Marquee speed={45} gradient={false}>
          {Array(6)
            .fill(0)
            .map((_, i) => (
              <span key={i} className="font-display text-2xl md:text-3xl uppercase tracking-tight mx-8 text-[#9AA3AF]">
                Marketing <span className="text-[#5E7E9B]">✦</span> Public Relations{" "}
                <span className="text-[#8AA189]">✦</span> Events{" "}
                <span className="text-[#5E7E9B]">✦</span> Social Strategy{" "}
                <span className="text-[#8AA189]">✦</span>
              </span>
            ))}
        </Marquee>
      </div>
    </section>
  );
};
